import * as React from 'react';
import './Node.css';
import Node from './Node';
import Edge from './Edge';
import HorizontalEdge from './HorizontalEdge';
import Loading from '../../components/loading/Loading';
import { useGraph } from '../../contexts/GraphContext';

const GraphViewPort = ({ viewPortSize, nodes, edges, minScale, initScale, isHorizontal }) => {

    const containerRef = React.useRef(null);

    const {
        setSelectedNode,
        scale,
        setScale,
        position,
        setPosition,
        boundaries,
        setBoundaries,
        loading,
        defaultBoundaries,
        graphSize,
        allowMovement,
        resetGraph,
    } = useGraph();

    // View port dragging logic
    const [dragging, setDragging] = React.useState(false);
    // TODO should be using userRef since State update is delay
    const lastMousePositionRef = React.useRef({ x: 0, y: 0 });
    const [enterNode, setEnterNode] = React.useState(null);

    const [lastTouchDistance, setLastTouchDistance] = React.useState(null);
    const [initialScale, setInitialScale] = React.useState(scale);

    const handleNodeClick = (nodeId) => {
        setSelectedNode(nodeId);
    };

    const handleNodeEnter = (nodeId) => {
        setEnterNode(nodeId);
    };

    const handleNodeLeave = () => {
        setEnterNode(null);
    };

    const handleWheel = (event) => {
        //event.preventDefault();
        if (!allowMovement) return;

        const zoomSpeed = 0.08;
        const newScale = Math.min(
            Math.max(scale + (event.deltaY < 0 ? zoomSpeed : -zoomSpeed), minScale),
            2
        );

        const rect = containerRef.current.getBoundingClientRect();

        const x = event.clientX - rect.left; // x position within the element.
        const y = event.clientY - rect.top;  // y position within the element.

        const newPosX = x - (x - position.x) * (newScale / scale);
        const newPosY = y - (y - position.y) * (newScale / scale);
        setPosition({ x: newPosX, y: newPosY });

        const maxX = defaultBoundaries.maxX * newScale;
        const minX = parseInt(viewPortSize.w) - ((300 + graphSize.w) * newScale);
        const maxY = defaultBoundaries.maxY * newScale;
        const minY = parseInt(viewPortSize.h) - ((170 + graphSize.h) * newScale);

        setBoundaries({ minX, maxX, minY, maxY });

        setScale(newScale);
    };


    const handleMouseDown = (event) => {
        if (!allowMovement) event.preventDefault(); // Prevent dragging highlight text unless modal is open
        setDragging(true);
        lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleMouseUp = () => {
        setDragging(false);
    };

    const handleMouseLeave = () => {
        setDragging(false);
    };

    const handleMouseMove = (event) => {
        if (!dragging) return;
        if (!allowMovement) return;

        const deltaX = event.clientX - lastMousePositionRef.current.x;
        const deltaY = event.clientY - lastMousePositionRef.current.y;

        setPosition((prev) => {
            let newX = prev.x + deltaX;
            let newY = prev.y + deltaY;

            // Apply constraints
            if (newX > boundaries.maxX + parseInt(viewPortSize.w) / 2)
                newX = boundaries.maxX + parseInt(viewPortSize.w) / 2;
            if (newX < boundaries.minX - parseInt(viewPortSize.w) / 2)
                newX = boundaries.minX - parseInt(viewPortSize.w) / 2;
            if (newY > boundaries.maxY + parseInt(viewPortSize.h) / 2)
                newY = boundaries.maxY + parseInt(viewPortSize.h) / 2;
            if (newY < boundaries.minY - parseInt(viewPortSize.h) / 2)
                newY = boundaries.minY - parseInt(viewPortSize.h) / 2;

            return { x: newX, y: newY };
        });

        // Update the ref synchronously
        lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleTouchStart = (event) => {
        if (!allowMovement) return;

        if (event.touches.length === 1) {
            // Handle panning
            setDragging(true);
            const touch = event.touches[0];
            lastMousePositionRef.current = { x: touch.clientX, y: touch.clientY };
            handleMouseDown({
                clientX: touch.clientX,
                clientY: touch.clientY,
                preventDefault: () => { },
            });
        } else if (event.touches.length === 2) {
            // Handle pinch-to-zoom
            event.preventDefault();
            setDragging(false);
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];

            const distance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            );
            setLastTouchDistance(distance);
            setInitialScale(scale);
        }
    };

    const handleTouchMove = (event) => {
        if (!allowMovement) return;

        if (event.touches.length === 1 && dragging) {
            // Handle panning
            event.preventDefault(); // Prevent default behavior
            const touch = event.touches[0];

            const deltaX = touch.clientX - lastMousePositionRef.current.x;
            const deltaY = touch.clientY - lastMousePositionRef.current.y;

            setPosition((prev) => ({
                x: prev.x + deltaX,
                y: prev.y + deltaY,
            }));

            lastMousePositionRef.current = { x: touch.clientX, y: touch.clientY };
        } else if (event.touches.length === 2) {
            // Handle pinch-to-zoom
            event.preventDefault();
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];

            const newDistance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            );

            if (lastTouchDistance !== null) {
                const distanceDelta = newDistance - lastTouchDistance;
                const zoomFactor = newDistance / lastTouchDistance;
                const newScale = Math.min(Math.max(initialScale * zoomFactor, minScale), 2);

                // Calculate the midpoint between the two touches
                const rect = containerRef.current.getBoundingClientRect();
                const midX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
                const midY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

                const newPosX = midX - (midX - position.x) * (newScale / scale);
                const newPosY = midY - (midY - position.y) * (newScale / scale);

                setPosition({ x: newPosX, y: newPosY });
                setScale(newScale);
            }
        }
    };

    const handleTouchEnd = (event) => {
        if (event.touches.length === 0) {
            // All touches have ended
            setDragging(false);
            setLastTouchDistance(null);
        } else if (event.touches.length === 1) {
            // One touch remains, reset for panning
            const touch = event.touches[0];
            lastMousePositionRef.current = { x: touch.clientX, y: touch.clientY };
            setLastTouchDistance(null);
            setInitialScale(scale);
        }
    };

    const outerContainerStyles = {
        position: 'relative',
        width: viewPortSize.w,
        height: viewPortSize.h,
        overflow: 'hidden',
        backgroundSize: `${30 * scale}px ${30 * scale}px`,
        backgroundImage: `radial-gradient(circle, rgba(0, 0, 0, 0.06) ${3 * scale}px, transparent ${0 * scale}px)`,
        backgroundPosition: `${position.x}px ${position.y}px, ${10 * scale - position.x}px ${10 * scale - position.y}px`,
        touchAction: 'none', // Add this line
    };

    const graphStyles = {
        position: 'absolute',
        width: viewPortSize.w,
        height: viewPortSize.h,
        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
        transformOrigin: '0 0'
    };

    const svgStyles = {
        position: 'absolute',
        width: viewPortSize.w,
        height: viewPortSize.h,
        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
        overflow: 'visible',
        transformOrigin: '0 0'
    };

    return (
        <div ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onWheel={handleWheel}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={outerContainerStyles}
        >
            {loading && <Loading />}

            {!loading && nodes.length === 0 && (
                <div className="not-found-container">
                    <div>
                        <h2>No topic to display</h2>
                        <p>
                            The topic or path you were looking for has no available topic.
                        </p>
                    </div>
                    {/*<div>*/}
                    {/*    <h2>Topic or Path Not Found</h2>*/}
                    {/*    <p>*/}
                    {/*        The topic or path you were looking for is not available.*/}
                    {/*    </p>*/}
                    {/*</div>*/}
                </div>
            )}
            {/*<defs>*/}
            {/*    <marker*/}
            {/*        id="arrow"*/}
            {/*        markerWidth="5"*/}
            {/*        markerHeight="5"*/}
            {/*        refX="3.5"*/}
            {/*        refY="2.5"*/}
            {/*        orient="auto"*/}
            {/*    >*/}
            {/*        <polygon points="0 0, 5 2.5, 0 5" fill="currentColor" />*/}
            {/*    </marker>*/}
            {/*</defs>*/}
            {!loading && (
                <svg style={svgStyles}>
                    {edges
                        .sort((a, b) => {
                            // Determine if they are focused
                            const aIsFocus = a.start === enterNode || a.end === enterNode;
                            const bIsFocus = b.start === enterNode || b.end === enterNode;

                            // If one is focused and the other is not, focused goes last (so it appears on top)
                            if (aIsFocus && !bIsFocus) return 1;
                            if (!aIsFocus && bIsFocus) return -1;

                            // If both have the same focus state, sort by edgeType: 
                            // Transparent (0) before dashed (1) so dashed appears last (on top)
                            if (a.edgeType !== b.edgeType) {
                                return a.edgeType - b.edgeType; // 0 before 1
                            }

                            return 0;
                        })
                        .map((edge, index) => (
                            <Edge enterNode={enterNode} key={index} {...edge} />
                        ))
                    }
                </svg>
            )}
            {!loading && <div style={graphStyles}>
                {nodes.map((node) => (
                    <Node key={node.id} id={node.topicId} status={"active"} onClick={handleNodeClick} onEnter={handleNodeEnter} onLeave={handleNodeLeave}  {...node} />
                ))}
            </div>}
        </div>
    );
};

export default GraphViewPort ;